import React from "react";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import _ from "lodash";
import { COMPANY_MANAGER_PROFILES, USER_MANAGER_PROFILES, hasAnyProfile, hasPlugin, hasProfile } from "app/utils/appHelpers";
import { canUseDownloads, canUseReports } from "app/shared/downloads/permissions";

const menus = (user) => {
  const availableItems = getAvailableMenuItems(user);
  return buildMenuStructure(availableItems);
};

const getAvailableMenuItems = (user) => {
  return allMenuItems.filter(item => !item.condition || item.condition(user));
};

const buildMenuStructure = (menuItems) => {
  const grouped = _.groupBy(menuItems, 'section');
  const sections = [];

  for (const section in grouped) {
    if (grouped.hasOwnProperty(section)) {
      sections.push({
        label: `sidebar.menu.${section}`,
        type: 'section',
        children: grouped[section].map(({ uri, label, icon }) => ({
          uri,
          label,
          type: 'nav-item',
          icon
        }))
      });
    }
  }

  return sections;
};

const allMenuItems = [
  {
    section: 'automations',
    uri: "/automations/dashboards",
    label: 'Dashboards',
    icon: <ShowChartOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations')
  },
  {
    section: 'automations',
    uri: "/automations/stores",
    label: 'sidebar.menuItem.stores',
    icon: <StorefrontOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations')
  },
  {
    section: 'automations',
    uri: "/automations/devices",
    label: 'sidebar.menuItem.devices',
    icon: <SensorsOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations')
  },
  {
    section: 'automations',
    uri: "/automations/rules",
    label: 'sidebar.menuItem.automation_rules',
    icon: <SettingsSuggestOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations')
  },
  {
    section: 'automations',
    uri: "/automations/notifications",
    label: 'sidebar.menuItem.notification_history',
    icon: <NotificationsActiveOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations')
  },
  {
    section: 'automations',
    uri: "/automations/reports",
    label: 'sidebar.menuItem.reports',
    icon: <AssessmentOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations') && canUseReports(user)
  },
  {
    section: 'automations',
    uri: "/automations/downloads",
    label: 'sidebar.menuItem.downloads',
    icon: <DownloadOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasPlugin(user, 'core-automations') && canUseDownloads(user)
  },
  {
    section: 'admin',
    uri: "/plugins",
    label: 'sidebar.menuItem.plugins',
    icon: <AppsOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasAnyProfile(user, COMPANY_MANAGER_PROFILES)
  },
  {
    section: 'admin',
    uri: "/users",
    label: 'sidebar.menuItem.users',
    icon: <PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasAnyProfile(user, USER_MANAGER_PROFILES)
  },
  {
    section: 'admin',
    uri: "/company_settings",
    label: 'sidebar.menuItem.company_settings',
    icon: <SettingsOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasAnyProfile(user, COMPANY_MANAGER_PROFILES)
  },
  {
    section: 'superadmin',
    uri: "/superadmin/companies",
    label: 'sidebar.menuItem.superadmin_companies',
    icon: <ApartmentOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/devices",
    label: 'sidebar.menuItem.superadmin_devices',
    icon: <SettingsInputComponentOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/gateways",
    label: 'sidebar.menuItem.superadmin_gateways',
    icon: <RouterOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/vision",
    label: 'sidebar.menuItem.superadmin_vision',
    icon: <VideocamOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/demo-requests",
    label: 'Demo requests',
    icon: <ScienceOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/users",
    label: 'sidebar.menuItem.superadmin_users',
    icon: <ManageAccountsOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/plugins",
    label: 'sidebar.menuItem.superadmin_plugins',
    icon: <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  },
  {
    section: 'superadmin',
    uri: "/superadmin/device-types",
    label: 'sidebar.menuItem.superadmin_device_types',
    icon: <MemoryOutlinedIcon sx={{ fontSize: 20 }} />,
    condition: (user) => hasProfile(user, 'superadmin')
  }
];

export default menus;
